import React, { useEffect, useState, createContext, useContext } from "react";
import { useDispatch } from "react-redux";
import { auth } from "../firebase/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { logout } from "../store/slices/session/sessionSlice";
import { TokenService } from "../services/tokenService";

// Create a context to share auth initialization state
interface AuthContextType {
  isAuthInitialized: boolean;
  currentPath?: string;
}

export const AuthContext = createContext<AuthContextType>({
  isAuthInitialized: false,
});

// Custom hook to consume the auth context
export const useAuth = () => useContext(AuthContext);

/**
 * Provider component that syncs Firebase auth state with Redux
 * This initializes the Firebase auth listener and keeps the Redux state in sync
 */
const FirebaseAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);
  const [currentPath, setCurrentPath] = useState<string>(
    window.location.pathname
  );

  // Log current path when it changes
  useEffect(() => {
    const handleLocationChange = () => {
      const newPath = window.location.pathname;
      console.log(`Path changed from ${currentPath} to ${newPath}`);
      setCurrentPath(newPath);
    };

    // Log initial path
    console.log(`Initial path: ${currentPath}`);

    // Listen for path changes
    window.addEventListener("popstate", handleLocationChange);

    return () => {
      window.removeEventListener("popstate", handleLocationChange);
    };
  }, [currentPath]);

  useEffect(() => {
    console.log("Setting up Firebase auth state listener");

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log(`Auth state changed at path: ${window.location.pathname}`);
      console.log(`User is ${user ? "signed in" : "signed out"}`);

      if (user) {
        try {
          // Use TokenService to update Redux store with user info and token
          await TokenService.updateUserInStore(dispatch);

          // Log success message
          console.log(
            "Firebase auth state synced to Redux - user is signed in"
          );
          console.log(`User email: ${user.email}, UID: ${user.uid}`);
          console.log(
            `Current path during auth update: ${window.location.pathname}`
          );
        } catch (error) {
          console.error("Error getting user token:", error);
          // Display an error but don't log out automatically - the token refresh mechanism might recover
          console.warn(
            "Will attempt to recover or refresh token via other mechanisms"
          );
        }
      } else {
        // User is signed out
        console.log("Firebase auth state synced to Redux - user is signed out");
        console.log(`Current path during logout: ${window.location.pathname}`);

        // Special case for public routes - don't dispatch logout if we're on a public route
        const publicRoutes = [
          "/",
          "/privacy-policy",
          "/terms-and-conditions",
          "/login",
          "/signup",
          "/forgot-password",
        ];
        const currentPath = window.location.pathname;

        if (publicRoutes.includes(currentPath)) {
          console.log(
            `On public route ${currentPath} - not dispatching logout`
          );
        } else {
          console.log(`Not on public route - dispatching logout`);
          dispatch(logout());
        }
      }

      // Mark authentication as initialized after first check
      if (!isAuthInitialized) {
        console.log("Auth initialization completed");
        setIsAuthInitialized(true);
      }
    });

    // Clean up subscription
    return () => unsubscribe();
  }, [dispatch, isAuthInitialized]);

  return (
    <AuthContext.Provider value={{ isAuthInitialized, currentPath }}>
      {children}
    </AuthContext.Provider>
  );
};

export default FirebaseAuthProvider;
